import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  createTableColumn,
  Text,
  Avatar,
  Badge,
  mergeClasses,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import type {
  TableColumnDefinition,
  DataGridProps,
  OnSelectionChangeData,
  SortDirection,
  TableColumnId,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import type { PlannerEventRow } from '@/types/planner.types';
import { normalizeEmail } from '@/utils/normalizeEmail';
import type { OwnerPhotoCacheEntry } from '@/hooks/useOwnerPhotos';
import type { EventState } from '@/store/models/events/event-state';
import {
  useStatusTagStyles,
  getStatusStyle,
} from '@/components/dashboard/StatusTag/StatusTag.styles';
import { STATUS_TO_EVENT_STATE } from '@/utils/plannerEventMapper';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { useEventDataGridStyles } from './EventDataGrid.styles';
import { LoadSentinel } from '@components/common/InfiniteScroll';

export interface EventDataGridSortState {
  sortColumn: TableColumnId | undefined;
  sortDirection: SortDirection;
}

export interface EventDataGridProps {
  items: PlannerEventRow[];
  loading?: boolean;
  isLoadingMore?: boolean;
  isError?: boolean;
  batchError?: boolean;
  endReached?: boolean;
  selectedItems?: Set<string>;
  onRowClick?: (eventId: string) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onReachEnd?: () => void;
  onRetry?: () => void;
  onRetryBatch?: () => void;
  onSort?: (
    sortBy: 'eventStartDate' | 'createdAt' | 'lastUpdatedAt',
    sortOrder: 'asc' | 'desc'
  ) => void;
  /**
   * Optional controlled sort state. When both `sortState` and `onSortChange` are
   * supplied the grid runs in controlled mode; otherwise it stays uncontrolled
   * (back-compat).
   */
  sortState?: EventDataGridSortState;
  onSortChange?: (state: EventDataGridSortState) => void;
  pageSize?: number;
  /**
   * Map of normalized (lowercased + trimmed) owner email to cached profile-photo entry.
   * When present and an event owner's email resolves to a `hit` entry, the owner cell
   * renders the user's Graph profile photo; otherwise the existing initials avatar is
   * shown. The prop is optional — omitting it preserves the original initials-only
   * rendering (graceful degradation, FR-014).
   */
  ownerPhotoMap?: ReadonlyMap<string, OwnerPhotoCacheEntry>;
}

const SKELETON_ROW_COUNT = 10;
const INCREMENTAL_SHIMMER_ROW_COUNT = 5;
const SHIMMER_FLICKER_DELAY_MS = 100;
const SKELETON_CELL_COUNT = 10;
const SCROLL_IDLE_FADE_MS = 800;
const DEFAULT_PAGE_SIZE = 10;
const FALLBACK_TEXT = '-';

const KNOWN_STATUSES = new Set<PlannerEventRow['status']>(
  Object.keys(STATUS_TO_EVENT_STATE) as PlannerEventRow['status'][]
);

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isDateRangeString(value: unknown): value is string {
  if (!hasText(value)) {
    return false;
  }

  const trimmed = value.trim();
  return /\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/.test(trimmed) && trimmed.includes('-');
}

const COLUMN_SIZING_OPTIONS = {
  event: { minWidth: 150, defaultWidth: 300, idealWidth: 200 },
  buildingName: { minWidth: 110, defaultWidth: 150, idealWidth: 170 },
  conferenceRoom: { minWidth: 130, defaultWidth: 170, idealWidth: 170 },
  requestedBy: { minWidth: 110, defaultWidth: 140, idealWidth: 160 },
  fteSponsor: { minWidth: 110, defaultWidth: 140, idealWidth: 160 },
  submittedDate: { minWidth: 120, defaultWidth: 130, idealWidth: 140 },
  eventDate: { minWidth: 130, defaultWidth: 250, idealWidth: 250 },
  status: { minWidth: 110, defaultWidth: 170, idealWidth: 200 },
  owner: { minWidth: 100, defaultWidth: 150, idealWidth: 170 },
  attendeeType: { minWidth: 120, defaultWidth: 140, idealWidth: 150 },
};

function getRowId(item: PlannerEventRow) {
  return item.eventId;
}

export const EventDataGrid: React.FC<EventDataGridProps> = ({
  items,
  loading = false,
  isLoadingMore = false,
  isError = false,
  batchError = false,
  endReached = false,
  selectedItems: selectedItemsProp,
  onRowClick,
  onSelectionChange,
  onReachEnd,
  onRetryBatch,
  onSort: _onSort,
  sortState,
  onSortChange,
  onRetry,
  pageSize: _pageSize = DEFAULT_PAGE_SIZE,
  ownerPhotoMap,
}) => {
  const styles = useEventDataGridStyles();
  const statusTagStyles = useStatusTagStyles();
  const { t } = useTranslation('planner');
  const showBottomShimmer = useDelayedFlag(isLoadingMore, SHIMMER_FLICKER_DELAY_MS);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const ownerPhotoMapRef = useRef(ownerPhotoMap);
  ownerPhotoMapRef.current = ownerPhotoMap;
  const selectedItems = useMemo(() => selectedItemsProp ?? new Set<string>(), [selectedItemsProp]);

  // A11y fix: Tabster injects dummy <i> elements as direct children of the
  // DataGrid (role="grid"). axe-core flags them as invalid grid children
  // (aria-required-children / WCAG 1.3.1). Fix: remove tabindex and role so
  // they become inert, and ensure aria-hidden="true" so axe skips them
  // entirely when checking required children.
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    const grid = container.querySelector<HTMLElement>('[role="grid"]');
    if (!grid) return;

    const patchDummies = () => {
      grid.querySelectorAll<HTMLElement>(':scope > [data-tabster-dummy]').forEach((el) => {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.setAttribute('aria-hidden', 'true');
      });
    };
    patchDummies();

    const observer = new MutationObserver(() => patchDummies());
    observer.observe(grid, { childList: true, attributes: true, subtree: false });
    return () => observer.disconnect();
  });

  // Toggle data-scrolling on the scroll container while the user is actively
  // scrolling. CSS transitions fade the scrollbar in on toggle-on and out on
  // toggle-off. Pure DOM mutation to avoid re-rendering on every scroll event.
  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      node.setAttribute('data-scrolling', 'true');
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        node.removeAttribute('data-scrolling');
      }, SCROLL_IDLE_FADE_MS);
    };
    node.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      node.removeEventListener('scroll', onScroll);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, []);

  const columns: TableColumnDefinition<PlannerEventRow>[] = useMemo(
    () => [
      createTableColumn<PlannerEventRow>({
        columnId: 'event',
        compare: (a, b) => a.eventName.localeCompare(b.eventName),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.event')}</Text>
        ),
        renderCell: (item) => (
          <div className={styles.twoLineCell} title={`${item.eventName} — ${item.referenceId}`}>
            <Text className={styles.primaryText}>
              {hasText(item.eventName) ? item.eventName : FALLBACK_TEXT}
            </Text>
            <Text className={styles.secondaryText}>
              {hasText(item.referenceId) ? item.referenceId : FALLBACK_TEXT}
            </Text>
          </div>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'buildingName',
        compare: (a, b) => a.buildingName.localeCompare(b.buildingName),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.buildingName')}</Text>
        ),
        renderCell: (item) => (
          <Text
            className={styles.primaryText}
            title={hasText(item.buildingName) ? item.buildingName : FALLBACK_TEXT}
          >
            {hasText(item.buildingName) ? item.buildingName : FALLBACK_TEXT}
          </Text>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'conferenceRoom',
        compare: (a, b) => a.conferenceRoom.localeCompare(b.conferenceRoom),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.conferenceRoom')}</Text>
        ),
        renderCell: (item) => (
          <span className={styles.roomCell}>
            <Text
              className={styles.primaryText}
              title={hasText(item.conferenceRoom) ? item.conferenceRoom : FALLBACK_TEXT}
            >
              {hasText(item.conferenceRoom) ? item.conferenceRoom : FALLBACK_TEXT}
            </Text>
            {item.additionalRooms.length > 0 && (
              <Badge
                className={styles.roomChip}
                appearance="tint"
                color="informative"
                title={item.additionalRooms.join(', ')}
                aria-label={item.additionalRooms.join(', ')}
              >
                {t('eventDataGrid.moreRooms', { count: item.additionalRooms.length })}
              </Badge>
            )}
          </span>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'requestedBy',
        compare: (a, b) => a.requestedBy.localeCompare(b.requestedBy),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.requestedBy')}</Text>
        ),
        renderCell: (item) => (
          <Text
            className={styles.primaryText}
            title={hasText(item.requestedBy) ? item.requestedBy : FALLBACK_TEXT}
          >
            {hasText(item.requestedBy) ? item.requestedBy : FALLBACK_TEXT}
          </Text>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'fteSponsor',
        compare: (a, b) => a.fteSponsor.localeCompare(b.fteSponsor),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.fteSponsor')}</Text>
        ),
        renderCell: (item) => (
          <Text
            className={styles.primaryText}
            title={hasText(item.fteSponsor) ? item.fteSponsor : FALLBACK_TEXT}
          >
            {hasText(item.fteSponsor) ? item.fteSponsor : FALLBACK_TEXT}
          </Text>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'submittedDate',
        compare: (a, b) => a.submittedDateRaw.localeCompare(b.submittedDateRaw),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.submittedDate')}</Text>
        ),
        renderCell: (item) => (
          <Text
            className={styles.primaryText}
            title={hasText(item.submittedDate) ? item.submittedDate : FALLBACK_TEXT}
          >
            {hasText(item.submittedDate) ? item.submittedDate : FALLBACK_TEXT}
          </Text>
        ),
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'eventDate',
        compare: (a, b) => a.eventDateRaw.localeCompare(b.eventDateRaw),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.eventDate')}</Text>
        ),
        renderCell: (item) => {
          const fallbackFromStatus = isDateRangeString(item.status) ? item.status : undefined;
          const eventDateText = hasText(item.eventDate)
            ? item.eventDate
            : (fallbackFromStatus ?? FALLBACK_TEXT);
          return (
            <Text className={styles.primaryText} title={eventDateText}>
              {eventDateText}
            </Text>
          );
        },
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'status',
        compare: (a, b) => a.status.localeCompare(b.status),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.status')}</Text>
        ),
        renderCell: (item) => {
          const tagStyles = statusTagStyles;
          const safeStatus: PlannerEventRow['status'] = KNOWN_STATUSES.has(item.status)
            ? item.status
            : 'Pending';
          const eventState: EventState =
            (STATUS_TO_EVENT_STATE as Record<string, EventState>)[safeStatus] ?? 'DRAFT';
          const statusLabelKey = `eventDataGrid.statuses.${eventState}`;
          const translatedStatusLabel = t(statusLabelKey);
          const statusLabel =
            translatedStatusLabel !== statusLabelKey ? translatedStatusLabel : safeStatus;
          return (
            <span
              className={mergeClasses(
                tagStyles.badge,
                getStatusStyle(eventState, tagStyles),
                styles.statusBadge
              )}
              title={statusLabel}
            >
              {statusLabel}
            </span>
          );
        },
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'owner',
        compare: (a, b) => a.owner.name.localeCompare(b.owner.name),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.owner')}</Text>
        ),
        renderCell: (item) => {
          const ownerName = hasText(item.owner?.name) ? item.owner.name.trim() : 'Unassigned';
          const normalizedEmail = normalizeEmail(item.owner.email);
          const cachedPhotoEntry = normalizedEmail
            ? ownerPhotoMapRef.current?.get(normalizedEmail)
            : undefined;
          const cachedPhotoUrl =
            cachedPhotoEntry?.status === 'hit' ? cachedPhotoEntry.dataUrl : undefined;
          const imageSrc = cachedPhotoUrl ?? item.owner.avatarUrl;
          return (
            <div className={styles.personaCell} title={ownerName}>
              <span aria-hidden="true">
                <Avatar
                  name={imageSrc ? undefined : ownerName}
                  color={imageSrc ? undefined : 'colorful'}
                  image={
                    imageSrc
                      ? {
                          src: imageSrc,
                          alt: t('eventDataGrid.avatar.photoOf', { name: ownerName }),
                        }
                      : undefined
                  }
                  size={20}
                />
              </span>
              <Text className={styles.personaName}>{ownerName}</Text>
            </div>
          );
        },
      }),
      createTableColumn<PlannerEventRow>({
        columnId: 'attendeeType',
        compare: (a, b) => a.attendeeType.localeCompare(b.attendeeType),
        renderHeaderCell: () => (
          <Text className={styles.headerCell}>{t('eventDataGrid.columns.attendeeType')}</Text>
        ),
        renderCell: (item) => (
          <Text
            className={styles.primaryText}
            title={hasText(item.attendeeType) ? item.attendeeType : FALLBACK_TEXT}
          >
            {hasText(item.attendeeType) ? item.attendeeType : FALLBACK_TEXT}
          </Text>
        ),
      }),
    ],
    [styles, statusTagStyles, t]
  );

  const handleSelectionChange = useCallback(
    (_e: unknown, data: OnSelectionChangeData) => {
      if (onSelectionChange) {
        const selectedIds = new Set<string>();
        data.selectedItems.forEach((id) => selectedIds.add(String(id)));
        onSelectionChange(selectedIds);
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.BUTTON_CLICK, {
          buttonName: 'eventDataGrid.selectionChange',
          selectedCount: String(selectedIds.size),
        });
      }
    },
    [onSelectionChange]
  );

  if (loading && items.length === 0) {
    return (
      <div
        className={styles.root}
        role="status"
        aria-label={t('eventDataGrid.loading')}
        aria-busy="true"
        data-testid="event-data-grid-loading"
      >
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
          <div key={i} className={styles.skeletonRow}>
            {Array.from({ length: SKELETON_CELL_COUNT }, (_, j) => (
              <Skeleton key={j} className={styles.skeletonCell}>
                <SkeletonItem size={16} />
              </Skeleton>
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorContainer} data-testid="event-data-grid-error">
        <Text className={styles.errorText}>{t('eventDataGrid.error')}</Text>
        {onRetry && (
          <BasicButton variant="primary" size="medium" onClick={onRetry}>
            {t('eventDataGrid.retry')}
          </BasicButton>
        )}
      </div>
    );
  }

  if (!loading && !isError && items.length === 0) {
    return (
      <div className={styles.errorContainer} data-testid="event-data-grid-empty">
        <Text className={styles.errorText}>{t('eventDataGrid.noResults')}</Text>
      </div>
    );
  }

  const sentinelDisabled = endReached || batchError || isLoadingMore || !onReachEnd;

  // Controlled-sort props passed to DataGrid only when both sortState and
  // onSortChange are supplied; otherwise the grid stays uncontrolled.
  const controlledSortProps =
    sortState && onSortChange
      ? {
          sortState,
          onSortChange: (
            _e: unknown,
            next: { sortColumn: TableColumnId | undefined; sortDirection: SortDirection }
          ) => onSortChange({ sortColumn: next.sortColumn, sortDirection: next.sortDirection }),
        }
      : {};

  return (
    <div
      ref={scrollContainerRef}
      className={styles.root}
      aria-busy={loading || isLoadingMore ? true : undefined}
    >
      {loading && items.length > 0 && (
        <div
          className={styles.loadingOverlay}
          role="status"
          aria-label={t('eventDataGrid.loading')}
          data-testid="event-data-grid-loading"
        >
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
            <div key={i} className={styles.skeletonRow}>
              {Array.from({ length: SKELETON_CELL_COUNT }, (_, j) => (
                <Skeleton key={j} className={styles.skeletonCell}>
                  <SkeletonItem size={16} />
                </Skeleton>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className={styles.gridContainer} ref={gridContainerRef}>
        <DataGrid
          items={items}
          columns={columns}
          selectionMode="multiselect"
          selectedItems={selectedItems}
          selectionAppearance={'checkbox' as 'none' | 'neutral' | 'brand'}
          sortable
          resizableColumns
          columnSizingOptions={COLUMN_SIZING_OPTIONS}
          getRowId={getRowId}
          onSelectionChange={handleSelectionChange as DataGridProps['onSelectionChange']}
          {...(controlledSortProps as Partial<DataGridProps>)}
          aria-label={t('eventDataGrid.ariaLabel')}
          data-testid="event-data-grid"
        >
          <DataGridHeader className={styles.stickyHeader}>
            <DataGridRow<PlannerEventRow>
              selectionCell={{ checkboxIndicator: { 'aria-label': 'Select all rows' } }}
            >
              {({ renderHeaderCell }) => (
                <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
              )}
            </DataGridRow>
          </DataGridHeader>
          <DataGridBody<PlannerEventRow>>
            {(row) => {
              const { item, rowId } = row;
              const selected = (row as unknown as { selected: boolean }).selected;
              return (
                <DataGridRow<PlannerEventRow>
                  key={rowId}
                  tabIndex={0}
                  className={mergeClasses(styles.row, selected ? styles.rowSelected : undefined)}
                  selectionCell={{
                    checkboxIndicator: { 'aria-label': `Select ${item.eventName}` },
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                    if (e.key === 'Enter') {
                      const target = e.target as HTMLElement;
                      const isCheckbox =
                        target.closest('[role="checkbox"]') ||
                        target.closest('input[type="checkbox"]') ||
                        target.closest('[data-selection-cell]');
                      if (isCheckbox) {
                        // Native checkboxes only respond to Space — simulate
                        // a click so Enter also toggles the selection.
                        e.preventDefault();
                        const clickable =
                          target.closest('[role="checkbox"]') ||
                          target.closest('input[type="checkbox"]');
                        (clickable as HTMLElement | null)?.click();
                        return;
                      }
                      e.preventDefault();
                      // Ensure the row itself is focused so the caller can
                      // capture document.activeElement as the restore target.
                      (e.currentTarget as HTMLElement).focus({ preventScroll: true });
                      onRowClick?.(item.eventId);
                      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
                        actionName: 'eventDataGrid.rowKeyboardActivate',
                        eventId: item.eventId,
                      });
                    }
                  }}
                  onClickCapture={(e: React.MouseEvent<HTMLElement>) => {
                    const target = e.target as HTMLElement;
                    const isCheckbox =
                      target.closest('[role="checkbox"]') ||
                      target.closest('input[type="checkbox"]') ||
                      target.closest('[data-selection-cell]');
                    if (!isCheckbox) {
                      e.stopPropagation();
                      e.preventDefault();
                      // Focus the row so document.activeElement is correct when
                      // the caller captures it as the focus-restore target.
                      (e.currentTarget as HTMLElement).focus({ preventScroll: true });
                      onRowClick?.(item.eventId);
                      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.NAVIGATION, {
                        actionName: 'eventDataGrid.rowClick',
                        eventId: item.eventId,
                      });
                    }
                  }}
                >
                  {({ renderCell }) => (
                    <DataGridCell className={styles.cell}>{renderCell(item)}</DataGridCell>
                  )}
                </DataGridRow>
              );
            }}
          </DataGridBody>
        </DataGrid>
        {onReachEnd && (
          <LoadSentinel
            onReachEnd={onReachEnd}
            disabled={sentinelDisabled}
            rootRef={scrollContainerRef}
            dataTestId="event-load-sentinel"
          />
        )}
        {showBottomShimmer && (
          <div className={styles.bottomShimmerRows} data-testid="event-data-grid-bottom-shimmer">
            {Array.from({ length: INCREMENTAL_SHIMMER_ROW_COUNT }, (_, i) => (
              <div key={i} className={styles.skeletonRow}>
                {Array.from({ length: SKELETON_CELL_COUNT }, (_, j) => (
                  <Skeleton key={j} className={styles.skeletonCell}>
                    <SkeletonItem size={16} />
                  </Skeleton>
                ))}
              </div>
            ))}
          </div>
        )}
        {batchError && (
          <div
            role="alert"
            className={styles.batchErrorRow}
            data-testid="event-data-grid-batch-error"
          >
            <Text>{t('eventDataGrid.batchError')}</Text>
            {onRetryBatch && (
              <BasicButton variant="primary" size="small" onClick={onRetryBatch}>
                {t('eventDataGrid.batchRetry')}
              </BasicButton>
            )}
          </div>
        )}
        <div
          className={styles.visuallyHidden}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="event-data-grid-live-region"
        >
          {showBottomShimmer ? t('eventDataGrid.loadingMore') : ''}
        </div>
      </div>
    </div>
  );
};

EventDataGrid.displayName = 'EventDataGrid';
